import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  usePrimaryComplaintStore,
  ComplaintHistoryStep,
  VisitReason,
  type ComplaintHeader,
} from "@/store/primaryComplaint";
import { useTaskStore } from "@/store/task";
import { askVasFor, complaintLimitFor } from "@/types/task-actions";
import { useProfileStore } from "@/store/profile";
import { finishHistoryStep } from "@/utils/navigateToFormHandler";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { ComplaintStepHeader, StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { complaintName, DEDICATED_REASON_ROUTES } from "@/utils/complaintSteps";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";

type Severity = { status: "loading" } | { status: "error" } | { status: "ready"; value: string | null };

/**
 * Recap after a complaint's outcome assessment: everything the patient has reported, each with an
 * Edit link, then "Add another complaint" (straight to the reason picker) or "I'm done".
 */
export function ComplaintSummaryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");
  const patientID = searchParams.get("patientID");
  const caseHistoryId = searchParams.get("caseHistoryId");

  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);
  const completeComplaint = usePrimaryComplaintStore((s) => s.completeComplaint);
  const fetchComplaints = usePrimaryComplaintStore((s) => s.fetchComplaints);
  const fetchComplaintValue = usePrimaryComplaintStore((s) => s.fetchComplaintValue);
  const startNewComplaint = usePrimaryComplaintStore((s) => s.startNewComplaint);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const setVisitReason = usePrimaryComplaintStore((s) => s.setVisitReason);
  const setHumanBody = usePrimaryComplaintStore((s) => s.setHumanBody);
  const wizardSteps = useTaskStore((s) => s.wizardSteps);
  // Case history config: cap on complaints, and whether the 1-10 rating is asked (no config: no cap, asked).
  const complaintLimit = useTaskStore((s) => complaintLimitFor(s.portalConfigMeta));
  const askVas = useTaskStore((s) => askVasFor(s.portalConfigMeta));
  // The recap can be reached with only caseHistoryId in the URL; the signed-in patient is the fallback
  // so Edit never opens a screen with an empty patientID (dedicated screens skip loading without it).
  const profilePatientID = useProfileStore((s) => s.currentPatient?.patientID?.toString() ?? "");

  // Marking this complaint complete is what lets the patient add another or finish; the store shows
  // a toast when it fails, and this screen offers the retry.
  const [completeStatus, setCompleteStatus] = useState<"saving" | "error" | "done">("saving");
  const [severity, setSeverity] = useState<Record<string, Severity>>({});

  const markComplete = useCallback(() => {
    if (!complaintId || (!patientID && !caseHistoryId)) {
      setCompleteStatus("done");
      return () => {};
    }
    let cancelled = false;
    setCompleteStatus("saving");
    completeComplaint(complaintId, true)
      .then(() => fetchComplaints(patientID ?? undefined))
      .then(() => {
        if (!cancelled) setCompleteStatus("done");
      })
      .catch(() => {
        if (!cancelled) setCompleteStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [complaintId, patientID, caseHistoryId, completeComplaint, fetchComplaints]);
  useEffect(() => markComplete(), [markComplete]);

  // Severity for every complaint, fetched in parallel; one failure only affects its own row.
  const loadSeverity = useCallback(
    (ids: string[]) => {
      setSeverity((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, { status: "loading" }])) }));
      for (const id of ids) {
        fetchComplaintValue(id, "CurrentSeverity")
          .then((v) => {
            const raw = typeof v === "string" ? v.trim() : "";
            const value = /^(10|[1-9])$/.test(raw) ? raw : null;
            setSeverity((prev) => ({ ...prev, [id]: { status: "ready", value } }));
          })
          .catch(() => setSeverity((prev) => ({ ...prev, [id]: { status: "error" } })));
      }
    },
    [fetchComplaintValue]
  );
  const headerIds = (complaintHeaders ?? []).map((h) => String(h.id)).join(",");
  useEffect(() => {
    if (askVas && headerIds) loadSeverity(headerIds.split(","));
  }, [askVas, headerIds, loadSeverity]);

  const search = caseHistoryId
    ? `?complaintId=${complaintId}&caseHistoryId=${caseHistoryId}`
    : `?complaintId=${complaintId}&patientID=${patientID ?? ""}`;

  const goBack = () => navigate({ pathname: "/complaint/outcome-assesment", search });

  const editComplaint = (header: ComplaintHeader) => {
    usePrimaryComplaintStore.setState({ currentComplaintHeader: header });
    setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory1);
    const type = (header.complaintType ?? "").toLowerCase() as VisitReason;
    if (Object.values(VisitReason).includes(type)) setVisitReason(type);
    if (header.region) {
      setHumanBody({
        selectedPart: { partName: "", side: "", label: header.region },
        selectedInternalPart: header.region,
      });
    }
    const params = new URLSearchParams({ complaintId: String(header.id), patientID: patientID || profilePatientID });
    if (caseHistoryId) params.set("caseHistoryId", caseHistoryId);
    const route = DEDICATED_REASON_ROUTES[type] ?? "/complaint/complaint-history";
    navigate(`${route}?${params.toString()}`);
  };

  const atComplaintLimit = (complaintHeaders?.length ?? 0) >= complaintLimit;

  const handleAddAnother = () => {
    if (atComplaintLimit) return;
    // Reset visit reason, body selection, and the history step so the next complaint starts clean
    // at step 1. ?add=1 opens the reason picker directly, so the patient isn't asked again.
    startNewComplaint();
    navigate({ pathname: "/complaint/primary-complaint", search: "?add=1" });
  };

  const handleDone = () => {
    finishHistoryStep(
      navigate,
      wizardSteps,
      "/complaint/complaint-summary",
      // No next history step → finish; never fall back to the hardcoded health-history pages.
      "/complaint/task-complete"
    ).catch(() => toast.error(t.finishFailed, { id: "history-finish-error" }));
  };

  const ready = completeStatus === "done";

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintId} step="recap" heading="Here's what you've told us" />

          {completeStatus === "error" && (
            <StepLoadError
              message="We couldn't finish saving this complaint. Your answers are safe."
              onRetry={markComplete}
            />
          )}

          {complaintHeaders && complaintHeaders.length > 0 && (
            <ul className="complaint-list__rows" aria-label="Your complaints">
              {complaintHeaders.map((h) => {
                const s = severity[String(h.id)];
                return (
                  <li key={String(h.id)} className="complaint-row">
                    <div className="complaint-row__main">
                      <span className="complaint-row__name">
                        {h.description?.trim() || complaintName(h.region, h.complaintType?.toLowerCase() ?? null) || "Complaint"}
                      </span>
                      {askVas && (
                        <p className="complaint-row__note">
                          {!s || s.status === "loading"
                            ? "Loading…"
                            : s.status === "error"
                              ? "Couldn't load severity."
                              : s.value
                                ? `Severity ${s.value} of 10`
                                : "Severity not rated"}
                        </p>
                      )}
                    </div>
                    <div className="complaint-row__actions">
                      {askVas && s?.status === "error" && (
                        <button type="button" className="text-link" onClick={() => loadSeverity([String(h.id)])}>
                          Try again
                        </button>
                      )}
                      <Button type="button" variant="outline" onClick={() => editComplaint(h)}>
                        Edit
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            {!atComplaintLimit && (
              <Button type="button" variant="outline" className="flex-1" onClick={handleAddAnother} disabled={!ready}>
                + Add another complaint
              </Button>
            )}
            <Button type="button" className="flex-1" onClick={handleDone} disabled={!ready}>
              I&apos;m done
            </Button>
          </div>
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>
          ← {t.backButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
